'use client';

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ADDONS } from '../constants/routes';
import { AuthContext } from '../store/ContextStore';
import localStorageService from '../services/localStorageService';
import subscriptionFeatures from '../constants/subscriptionFeatures';
import subscriptionCostService, { parseSubscriptionCost } from '../services/subscriptionCostService';
import PurchaseStepper from '../components/PurchaseStepper';

interface Sport {
  id: string;
  name: string;
  icon: string;
  gender: 'M' | 'W' | null;
}

const ALL_SPORTS: Sport[] = [
  { id: 'football', name: 'Football', icon: '🏈', gender: null },
  { id: 'basketball-m', name: 'Basketball', icon: '🏀', gender: 'M' },
  { id: 'basketball-w', name: 'Basketball', icon: '🏀', gender: 'W' },
  { id: 'ice-hockey-m', name: 'Ice Hockey', icon: '🏒', gender: 'M' },
  { id: 'ice-hockey-w', name: 'Ice Hockey', icon: '🏒', gender: 'W' },
  { id: 'volleyball-m', name: 'Volleyball', icon: '🏐', gender: 'M' },
  { id: 'volleyball-w', name: 'Volleyball', icon: '🏐', gender: 'W' },
  { id: 'soccer-m', name: 'Soccer', icon: '⚽', gender: 'M' },
  { id: 'soccer-w', name: 'Soccer', icon: '⚽', gender: 'W' },
];

const SPORT_CODE_TO_SUBSCRIPTION_ID: Record<string, string> = {
  MFB: 'football',
  MBB: 'basketball-m',
  WBB: 'basketball-w',
  MIH: 'ice-hockey-m',
  WIH: 'ice-hockey-w',
  MSO: 'soccer-m',
  WSO: 'soccer-w',
  MVB: 'volleyball-m',
  WVB: 'volleyball-w',
};

const SUBSCRIPTION_ID_TO_SPORT_CODE: Record<string, string> = {
  'football': 'MFB',
  'basketball-m': 'MBB',
  'basketball-w': 'WBB',
  'ice-hockey-m': 'MIH',
  'ice-hockey-w': 'WIH',
  'soccer-m': 'MSO',
  'soccer-w': 'WSO',
  'volleyball-m': 'MVB',
  'volleyball-w': 'WVB',
};

type EntitlementStatus = 'subscribed' | 'in_trial' | 'trial_expired' | 'none';

export default function AthlyteSportSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(AuthContext);
  const [continueError, setContinueError] = useState<string | null>(null);

  type PricingState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'available'; cost: number; currency: string }
    | { status: 'unavailable' }
    | { status: 'error'; message: string };

  const [pricingBySportId, setPricingBySportId] = useState<Record<string, PricingState>>({});

  const [selectedSports, setSelectedSports] = useState<string[]>(() => {
    const previouslySelected = location.state?.selectedSports;
    if (previouslySelected && previouslySelected.length > 0) {
      return previouslySelected.map((s: { id: string }) => s.id);
    }
    return [];
  });

  // Store addon state from Addons page to preserve selections when going back
  const [preservedAddons] = useState<Record<string, any>>(() => location.state?.addons || {});
  const [preservedAddonMetaBySportId] = useState<Record<string, any>>(() => location.state?.addonMetaBySportId || {});
  const [preservedIsAddonOnly] = useState<boolean>(() => location.state?.isAddonOnly || false);
  const [preservedBasePriceBySportId] = useState<Record<string, number>>(() => location.state?.basePriceBySportId || {});
  const [preservedCurrency] = useState<string | null>(() => location.state?.currency || null);

  const sportsById = useMemo(() => new Map(ALL_SPORTS.map((s) => [s.id, s])), []);

  const resolvedUser = useMemo(() => {
    const contextUser = auth?.user;
    return contextUser && Object.keys(contextUser).length > 0 ? contextUser : localStorageService.getUserInfo();
  }, [auth?.user]);

  const subscription = (resolvedUser as any)?.subscription;
  const sportAccess: Record<string, any> = subscription?.sport_access ?? subscription?.sportAccess ?? {};

  const teamSubStatusRaw =
    subscription?.sub_status ??
    subscription?.subStatus ??
    subscription?.team_sub_status ??
    subscription?.teamSubStatus;

  const isTeamSubscribed = useMemo(() => {
    if (teamSubStatusRaw === undefined || teamSubStatusRaw === null) return false;
    const v = String(teamSubStatusRaw).trim().toLowerCase();
    return ['active', 'subscribed', 'paid', 'succeeded', 'success'].includes(v);
  }, [teamSubStatusRaw]);

  const getEntitlementStatus = (sportId: string, nowMs: number = Date.now()): EntitlementStatus => {
    const sportCode = SUBSCRIPTION_ID_TO_SPORT_CODE[sportId];
    if (!sportCode) return 'none';

    const row = sportAccess?.[sportCode];
    if (!row || typeof row !== 'object') return 'none';

    const status = String(row?.status || '').trim().toLowerCase();
    const untilSec = Number(row?.until);
    const nowSec = Math.floor(nowMs / 1000);

    if (status === 'locked') return 'none';
    if (status === 'trial_end') return 'trial_expired';
    if (status === 'trial') {
      if (Number.isFinite(untilSec) && untilSec > 0 && untilSec <= nowSec) return 'trial_expired';
      return 'in_trial';
    }
    if (['active', 'subscribed', 'paid', 'succeeded', 'success'].includes(status)) return 'subscribed';
    if (isTeamSubscribed) return 'subscribed';

    return 'none';
  };

  const allowedSportIds = useMemo(() => {
    const codes: string[] = Array.isArray(resolvedUser?.sports)
      ? resolvedUser.sports.map((s: any) => s?.code).filter(Boolean)
      : [];

    const ids = codes
      .map((code) => SPORT_CODE_TO_SUBSCRIPTION_ID[code] ?? code)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);

    return Array.from(new Set(ids));
  }, [resolvedUser]);

  const sports: Sport[] = useMemo(() => {
    if (allowedSportIds.length === 0) return ALL_SPORTS;
    const idSet = new Set([...allowedSportIds, ...selectedSports]);
    return ALL_SPORTS.filter((s) => idSet.has(s.id));
  }, [allowedSportIds, selectedSports]);

  const handleSportClick = (sportId: string) => {
    const isSelected = selectedSports.includes(sportId);
    if (isSelected) {
      setSelectedSports((prev) => prev.filter((id) => id !== sportId));
      return;
    }

    setSelectedSports((prev) => [...prev, sportId]);
  };

  const fetchPricingForSportId = useCallback(async (sportId: string) => {
    const sportCode = SUBSCRIPTION_ID_TO_SPORT_CODE[sportId];
    if (!sportCode) {
      setPricingBySportId((prev) => ({ ...prev, [sportId]: { status: 'error', message: 'Unsupported sport.' } }));
      return;
    }

    setPricingBySportId((prev) => ({ ...prev, [sportId]: { status: 'loading' } }));
    const res: any = await subscriptionCostService.getSubscriptionCost(sportCode);

    const status = res?.status;
    if (status === 404) {
      setPricingBySportId((prev) => ({ ...prev, [sportId]: { status: 'unavailable' } }));
      return;
    }
    if (!status || status < 200 || status >= 300) {
      const msg = res?.data?.detail || res?.data?.message || 'Failed to load pricing.';
      setPricingBySportId((prev) => ({ ...prev, [sportId]: { status: 'error', message: String(msg) } }));
      return;
    }

    const { cost, currency } = parseSubscriptionCost(res?.data);
    if (cost === null || !currency) {
      setPricingBySportId((prev) => ({
        ...prev,
        [sportId]: { status: 'error', message: 'Invalid pricing response.' },
      }));
      return;
    }

    setPricingBySportId((prev) => ({ ...prev, [sportId]: { status: 'available', cost, currency } }));
  }, []);

  useEffect(() => {
    // Fetch pricing for any selected non-subscribed sports (e.g., when returning from other pages).
    selectedSports.forEach((sportId) => {
      const entitlement = getEntitlementStatus(sportId);
      if (entitlement === 'subscribed') return;
      const existing = pricingBySportId[sportId];
      if (existing && existing.status !== 'idle') return;
      void fetchPricingForSportId(sportId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSports]);

  const basePriceBySportId: Record<string, number> = {};
  let anyPricingUnavailable = false;
  let anyPricingLoading = false;
  let anyPricingError = false;

  const currencies = new Set<string>();
  selectedSports.forEach((sportId) => {
    const entitlement = getEntitlementStatus(sportId);
    if (entitlement === 'subscribed') {
      basePriceBySportId[sportId] = 0;
      return;
    }

    const p = pricingBySportId[sportId];
    if (!p || p.status === 'idle' || p.status === 'loading') {
      anyPricingLoading = true;
      return;
    }
    if (p.status === 'unavailable') {
      anyPricingUnavailable = true;
      return;
    }
    if (p.status === 'error') {
      anyPricingError = true;
      return;
    }
    basePriceBySportId[sportId] = p.cost;
    currencies.add(p.currency);
  });

  const resolvedCurrency = currencies.size === 1 ? Array.from(currencies)[0] : null;
  const currencyError = currencies.size > 1 ? 'Multiple currencies detected. Please contact support.' : null;
  const baseCost = Object.values(basePriceBySportId).reduce((sum, v) => sum + (Number(v) || 0), 0);
  const totalDue = baseCost;

  const canContinueToAddons =
    selectedSports.length > 0 &&
    !anyPricingUnavailable &&
    !anyPricingLoading &&
    !anyPricingError &&
    !currencyError;

  const showPricingUnavailableBanner = selectedSports.length > 0 && anyPricingUnavailable;

const handleBack = useCallback(() => {
  navigate(-1);
}, [navigate]);


  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#E8EBF3] via-[#F0F3FA] to-[#F8FAFC]">
      {/* Back Button */}
      <div className="max-w-6xl mx-auto px-4 pt-6 flex items-start">
        <button
          onClick={handleBack}
          className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
          aria-label="Go back"
        >
          <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
      <div className="w-full pb-16 flex flex-col items-center justify-center">
        <PurchaseStepper currentStep={1} className="px-4 mb-8" />

        {/* Hero Section */}
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-slate-900 mb-3 tracking-tight">
            Choose your sport
          </h2>
          <p className="text-base text-slate-600">
            Select the sports you want insights for —{' '}
            <span className="font-semibold text-slate-900">pricing is calculated at checkout</span>
          </p>
        </div>

        {/* Selection Summary Card */}
        {selectedSports.length > 0 && (
          <div className="mb-10">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white border-2 border-red-500 rounded-full shadow-md hover:shadow-lg transition-shadow">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-red-600 font-semibold">
                {selectedSports.length} sport{selectedSports.length > 1 ? 's' : ''} selected
              </span>
              <span className="text-slate-400">•</span>
              <span className="text-slate-900 font-bold">
                {resolvedCurrency ? `${totalDue} ${resolvedCurrency} /Year` : `$${totalDue}/year`}
              </span>
            </div>
          </div>
        )}

        {showPricingUnavailableBanner && (
          <div className="mb-8 max-w-3xl w-full px-8">
            <div className="bg-white border border-amber-200 rounded-xl px-5 py-4 text-amber-800 font-primary">
              Pricing unavailable for one or more selected sports. Contact support.
            </div>
          </div>
        )}

        {currencyError && (
          <div className="mb-8 max-w-3xl w-full px-8">
            <div className="bg-white border border-red-200 rounded-xl px-5 py-4 text-red-700 font-primary">
              {currencyError}
            </div>
          </div>
        )}

        <div className="w-full max-w-4xl px-8">
          {allowedSportIds.length > 0 && sports.length === 0 && (
            <div className="mb-8 rounded-xl border border-slate-200 bg-white px-5 py-4 text-center text-slate-700">
              No supported sports found on your account.
            </div>
          )}

          {/* Sports Grid */}
          <div className="grid grid-cols-3 gap-3 mb-10 justify-items-center">
            {sports.map((sport) => {
              const isSelected = selectedSports.includes(sport.id);
              const entitlement = getEntitlementStatus(sport.id);
              const showStatus =
                entitlement === 'subscribed' ||
                entitlement === 'in_trial' ||
                entitlement === 'trial_expired';
              const isSubscribedSport = entitlement === 'subscribed';

              const statusLabel =
                entitlement === 'subscribed'
                  ? 'SUBSCRIBED'
                  : entitlement === 'in_trial'
                    ? 'IN TRIAL'
                    : entitlement === 'trial_expired'
                      ? 'TRIAL EXPIRED'
                      : '';

              const statusClasses =
                entitlement === 'subscribed'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white'
                  : entitlement === 'in_trial'
                    ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-white'
                    : 'bg-gradient-to-r from-slate-500 to-slate-600 text-white';

              return (
	                <button
	                  key={sport.id}
	                  type="button"
	                  onClick={() => {
	                    handleSportClick(sport.id);
	                  }}
	                  className={`w-full max-w-sm relative group overflow-hidden rounded-2xl p-5 transition-all duration-300 transform hover:scale-105 ${
	                    isSelected
	                      ? 'bg-gradient-to-br from-red-50 to-red-50 border-2 border-red-500 shadow-lg'
	                      : 'bg-white border-2 border-slate-200 shadow-md hover:border-slate-300 hover:shadow-lg'
	                  }`}
                >
                  {showStatus && (
                    <div
                      className={`absolute top-0 left-0 right-0 py-1.5 text-center text-xs font-extrabold tracking-wider ${statusClasses}`}
                    >
                      {statusLabel}
                    </div>
                  )}
                  {isSelected && (
                    <div
                      className={`absolute ${showStatus ? 'top-9' : 'top-3'} left-3 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-lg`}
                    >
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  {sport.gender && (
                    <div className={`absolute ${showStatus ? 'top-9' : 'top-3'} right-3 px-2.5 py-1 rounded-md text-xs font-bold ${
                      sport.gender === 'M' ? 'bg-cyan-100 text-cyan-600' : 'bg-pink-100 text-pink-600'
                    }`}>
                      {sport.gender}
                    </div>
                  )}
                  <div className="flex flex-col items-center justify-center">
                    <div
                      className={`w-12 h-12 mb-2 inline-flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100 text-4xl leading-none group-hover:scale-110 group-hover:bg-white transition-all duration-300 ${showStatus ? 'mt-4' : ''}`}
                    >
                      <span aria-hidden="true">{sport.icon}</span>
                    </div>
                    <h3 className={`text-sm font-semibold transition-colors text-center ${
                      isSelected ? 'text-red-600' : 'text-slate-900'
                    }`}>
                      {sport.name}
                    </h3>

                    {isSubscribedSport && (
                      <div className="mt-3 px-3 py-1.5 text-xs font-extrabold rounded-lg bg-amber-50 border border-amber-200 text-amber-700">
                        Purchase Addons
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </button>
              );
            })}
          </div>

          {/* Features Included (always visible) */}
          <div className="mb-10">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-6">
              <h3 className="text-xl font-bold text-slate-900 mb-5">
                {subscriptionFeatures?.title || 'Features included'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(subscriptionFeatures?.columns || []).map((col: string[], idx: number) => (
                  <ul key={idx} className="space-y-2">
                    {(col || []).map((text: string) => (
                      <li key={text} className="flex items-start gap-2 text-slate-800">
                        <span className="mt-0.5 text-slate-600" aria-hidden="true">✓</span>
                        <span className="text-base">{text}</span>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
          </div>

          {/* Plan Validity (always visible) */}
          <div className="mb-10">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-6">
              <h3 className="text-xl font-bold text-slate-900 mb-5">Plan validity</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-800">
                  <span className="mt-0.5 text-slate-600" aria-hidden="true">✓</span>
                  <span className="text-base">Subscription purchase is valid for 1 year from the purchase date</span>
                </li>
                <li className="flex items-start gap-2 text-slate-800">
                  <span className="mt-0.5 text-slate-600" aria-hidden="true">✓</span>
                  <span className="text-base">Add-ons are purchased for lifetime</span>
                </li>
              </ul>
            </div>
          </div>

        </div>

        {/* CTA Section */}
        <div className="flex flex-col items-center gap-4 mt-6">
          <button
              onClick={() => {
                setContinueError(null);
                if (!canContinueToAddons) {
                  if (selectedSports.length === 0) {
                    setContinueError('Select at least one sport to continue.');
                  } else if (anyPricingLoading) {
                    setContinueError('Fetching pricing… please wait.');
                  } else if (anyPricingUnavailable) {
                    setContinueError('Pricing unavailable, contact support.');
                  } else if (anyPricingError) {
                    setContinueError('Pricing error. Please try again.');
                  } else if (currencyError) {
                    setContinueError(currencyError);
                  } else {
                    setContinueError('Unable to continue.');
                  }
                  return;
                }
                const selected = selectedSports
                  .map((id) => sportsById.get(id))
                  .filter(Boolean) as Sport[];
                // Determine if all selected sports are already subscribed (base price = 0)
                const allAreSubscribed = selected.every(sport => basePriceBySportId[sport.id] === 0);

                navigate(ADDONS, {
                  state: {
                    selectedSports: selected,
                    basePriceBySportId: Object.keys(basePriceBySportId).length ? basePriceBySportId : preservedBasePriceBySportId,
                    currency: preservedCurrency || resolvedCurrency,
                    addons: preservedAddons,
                    addonMetaBySportId: preservedAddonMetaBySportId,
                    // Only require addons if ALL selected sports are already subscribed
                    // If ANY new sport is being added, allow checkout without addons
                    isAddonOnly: allAreSubscribed ? preservedIsAddonOnly : false,
                  },
                });
              }}
              disabled={!canContinueToAddons}
              className={`px-10 py-3 font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                selectedSports.length > 0
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                  : 'bg-slate-200 text-slate-400'
              }`}
            >
              Continue
            </button>

          {continueError && (
            <p className="text-red-600 text-sm font-medium">{continueError}</p>
          )}

          <p className="text-slate-500 text-sm">
            {selectedSports.length === 0
              ? 'Select at least one sport to continue'
              : `Next: choose addons for ${selectedSports.length} sport${selectedSports.length > 1 ? 's' : ''}.`}
          </p>
        </div>

      </div>
    </div>
  );
}
